import { useCallback, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { AccountContext } from "auth/accountProvider";
import { type CpiAuth } from "lib/cpiQueries";
import { type RegionResponse } from "./regionMapOptions";

// Mirrors the useCpiAuth helper used by the other CPI data hooks.
const useCpiAuth = (): (() => Promise<CpiAuth>) => {
  const { getSession } = useContext(AccountContext);
  return useCallback(async (): Promise<CpiAuth> => {
    const session = await getSession();
    if (!session) throw new Error("Session expired. Please log in again.");
    const jwtToken = session.getAccessToken?.()?.getJwtToken?.();
    const sessionId = session.getIdToken?.()?.payload?.sessionId;
    if (!jwtToken || !sessionId) {
      throw new Error("Session tokens missing. Please log in again.");
    }
    return { jwtToken, sessionId };
  }, [getSession]);
};

export interface RegionQueryParams {
  startDate: string;
  endDate: string;
  clientId?: string[];
  carrierId?: string[];
  modality?: string[];
  /**
   * Passed through even though the endpoint cannot honour them. It reports them in
   * `ignoredFilters` so the UI can say the map is less filtered than its neighbours.
   * Dropping them here instead would hide that from the user.
   */
  fuelType?: string[];
  emissionSource?: string[];
  shipperId?: string[];
  tocs?: string[];
  /**
   * Always request the FINEST resolution. The client rolls up locally as the user
   * zooms, and it can only ever go coarser — asking the server for res 3 makes
   * zooming past it impossible, because there is no finer data to show.
   */
  resolution?: 2 | 3 | 4;
}

export const useRegionEmissions = (
  params: RegionQueryParams,
  enabled = true
) => {
  const getAuth = useCpiAuth();

  return useQuery({
    queryKey: ["cpi", "regions", params] as const,
    enabled,
    queryFn: async ({ signal }): Promise<RegionResponse> => {
      const { jwtToken, sessionId } = await getAuth();
      const res = await axios.get<RegionResponse>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/regions`,
        {
          params,
          headers: {
            Authorization: `Session ${jwtToken}`,
            "X-Session-Id": sessionId,
          },
          signal,
        }
      );
      return res.data;
    },
  });
};
